import { useState, useEffect, type FormEvent } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useCreateExternalTask } from '../../hooks/useCreateExternalTask';

export interface ExternalCreateTaskModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after the server confirms the create (after onSuccess fires). */
  onCreated?: (task: { id: number }) => void;
}

/**
 * Phase 25 Plan 25-07 D-21..D-23 — external-only task-create modal.
 *
 * Fields: name (required, text input) + description (optional, plain
 * textarea — markdown stored verbatim per CONTEXT D-22). Per CONTEXT D-22,
 * exposing the @mention picker inside the description is planner's
 * discretion; for v6.0 we keep the field simple and skip the picker.
 *
 * Phase 51 UAT #3 — no "Submitted as new" checkbox and no tier picker. Every
 * task an external client creates on an intake-enabled project is routed to the
 * Triage column server-side for internal triagers to prioritize and tier
 * (D-07 / T-51-70 — tier inflation by external clients is forbidden).
 *
 * Submit: POST /api/projects/:projectId/tasks (Phase 22 D-22). Backend forces
 * is_external=true and sets status='triage' / taskStateId=null when the project
 * has allowExternalIntake on (Plan 03 + UAT #3).
 *
 * Outcomes:
 *   - 2xx → onCreated fires (page scrolls first column into view), onClose
 *           fires, the form resets on next open.
 *   - 4xx/5xx → modal stays open, error surfaces above the buttons via
 *               role="alert" so screen readers announce it. Optimistic
 *               cache mutation rolls back automatically inside the hook.
 */
export function ExternalCreateTaskModal({
  projectId,
  isOpen,
  onClose,
  onCreated,
}: ExternalCreateTaskModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mutation = useCreateExternalTask();

  // Reset whenever the modal transitions to open.
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setErrorMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !mutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorMessage(null);
    const trimmedDesc = description.trim();
    mutation.mutate(
      {
        projectId,
        input: {
          name: trimmedName,
          description: trimmedDesc.length > 0 ? trimmedDesc : null,
        },
      },
      {
        onSuccess: (task: { id: number }) => {
          if (onCreated) onCreated({ id: task.id });
          onClose();
        },
        onError: (err: Error) => {
          setErrorMessage(err.message ?? 'Couldn’t create task');
        },
      },
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      ariaLabelledBy="ext-create-task-heading"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <h2
          id="ext-create-task-heading"
          className="text-lg font-semibold text-gray-900"
        >
          New task
        </h2>
        <label className="block text-sm font-medium text-gray-700">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={500}
            required
            autoFocus
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
            aria-label="Task name"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Description (optional)
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            aria-label="Task description"
          />
        </label>
        {errorMessage && (
          <p className="text-sm text-red-700" role="alert">
            {errorMessage}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            isLoading={mutation.isPending}
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
